import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Building2, ChevronsUpDown, Check, Plus, User } from 'lucide-react';
import { useOrgContext } from '@/providers/org-provider';
import { cn } from '@/lib/utils';

interface OrgSwitcherProps {
  /** When true, render only the org icon (sidebar collapsed mode). */
  collapsed?: boolean;
}

/**
 * Sidebar-mounted org switcher. Renders the current org's icon + name
 * (or just the icon when the sidebar is collapsed) and opens a dropdown
 * to switch orgs or create a new team org. Switching navigates to
 * `/orgs/<id>/dashboard`. Role is intentionally NOT shown — keeps the
 * trigger uncluttered and matches the rest of the nav.
 */
export function OrgSwitcher({ collapsed = false }: OrgSwitcherProps) {
  const { orgs, currentOrg, selectOrg } = useOrgContext();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Switching orgs while on an org-scoped route: swap the orgId and
  // preserve only the FIRST segment after it. Deeper IDs (portfolio,
  // project, run, etc.) belong to the previous org and won't exist in
  // the new one, so we drop them and land on the section's index page.
  //
  // Switching while on a global route (/learn, /settings, /notifications,
  // /admin, /home): no navigation. The org context flips via selectOrg,
  // and the sidebar's org-scoped nav re-renders pointing at the new org.
  function handleSwitch(newOrgId: string) {
    selectOrg(newOrgId);
    setOpen(false);
    const m = location.pathname.match(/^\/orgs\/[^/]+(\/[^/]+)?/);
    if (m) {
      const segment = m[1] ?? '/dashboard';
      navigate(`/orgs/${newOrgId}${segment}`);
    }
  }

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const label = currentOrg?.name ?? 'Select org';
  const kind = currentOrg?.kind ?? null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        title={collapsed ? label : undefined}
        className={cn(
          'w-full flex items-center gap-3 rounded-lg hover:bg-muted text-sm font-medium text-foreground transition-colors',
          collapsed ? 'p-2 justify-center' : 'px-3 py-2',
        )}
      >
        <span className="h-6 w-6 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
          {kind === 'team'
            ? <Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            : <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />}
        </span>
        {!collapsed && (
          <>
            <span className="flex-1 min-w-0 truncate text-left">{label}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-72 rounded-xl border border-border bg-card shadow-lg overflow-hidden',
            // Collapsed sidebar is left of dropdown trigger; expanded sidebar is wider, dropdown opens below.
            collapsed ? 'left-full ml-1 top-0' : 'left-0 top-full',
          )}
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border">
            Switch org
          </div>
          <div className="max-h-72 overflow-y-auto">
            {orgs.map((org) => (
              <button
                key={org.orgId}
                onClick={() => handleSwitch(org.orgId)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-left',
                  currentOrg?.orgId === org.orgId && 'bg-muted/60',
                )}
              >
                <span className="h-6 w-6 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                  {org.kind === 'team'
                    ? <Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    : <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />}
                </span>
                <span className="flex-1 min-w-0 truncate">{org.name}</span>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{org.kind}</span>
                {currentOrg?.orgId === org.orgId && <Check className="h-3.5 w-3.5 text-foreground" />}
              </button>
            ))}
          </div>
          <div className="border-t border-border">
            <Link
              to="/orgs/new"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted text-foreground"
            >
              <Plus className="h-3.5 w-3.5" />
              Create team org
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
